import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  styled,
  CircularProgress,
  Avatar,
  Divider,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import profileApi from "../api/profileApi";
import { useErrorHandler } from "../hooks/useErrorHandler";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-hot-toast";
import { languages } from '../const/languages';
import { useLanguage } from '../context/LanguageContext';

const ProfileSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: "0.875rem",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 400,
  fontSize: "1rem",
}));

function Profile() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingLanguage, setUpdatingLanguage] = useState(false);
  const handleError = useErrorHandler();
  const { language, updateLanguage } = useLanguage();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await profileApi.getProfile();
        setProfileData(data);
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [handleError]);

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;
    setUpdatingLanguage(true);
    try {
      const success = await updateLanguage(newLanguage);
      if (success) {
        setProfileData(prev => ({
          ...prev,
          preferredLanguage: newLanguage
        }));
        toast.success('Language preference updated successfully');
      }
    } catch (error) {
      console.error('Language update error:', error);
      toast.error('Failed to update language preference');
    } finally {
      setUpdatingLanguage(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography color="error">
          Failed to load profile data. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 3,
        margin: "auto",
        pt: "54px !important",
      }}
    >
      <ProfileSection elevation={0}>
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: (theme) => theme.palette.primary.main,
              }}
            >
              {profileData?.name?.charAt(0)}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              {profileData?.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {profileData?.email}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InfoLabel>Full Name</InfoLabel>
                  <InfoValue>{profileData?.name}</InfoValue>
                </Grid>
                <Grid item xs={12}>
                  <InfoLabel>Email Address</InfoLabel>
                  <InfoValue>{profileData?.email}</InfoValue>
                </Grid>
                {/* Add more fields as needed */}
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Account Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to="/reset-password"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    component="a"
                    href="https://speare.ai/contact-us"
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                  >
                    Request Account Deletion
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        <Card sx={{ 
          mb: 3, 
          backgroundColor: '#1E1E1E',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Language Preference
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={language || ''}
                  onChange={handleLanguageChange}
                  disabled={updatingLanguage}
                  sx={{
                    backgroundColor: '#2D2D2D',
                    color: 'white',
                    '& .MuiSelect-icon': {
                      color: updatingLanguage ? 'rgba(255, 255, 255, 0.5)' : 'white',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#404040',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: updatingLanguage ? '#404040' : '#be58da',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: updatingLanguage ? '#404040' : '#be58da',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#1E1E1E',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                  IconComponent={(props) => (
                    updatingLanguage ? 
                    <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> :
                    <Box component="span" className="MuiSelect-icon MuiSelect-iconOutlined" {...props}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                        <path d="M7 10l5 5 5-5z"/>
                      </svg>
                    </Box>
                  )}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img
                          src={`https://flagcdn.com/24x18/${lang.countryCode.toLowerCase()}.png`}
                          alt={lang.name}
                          style={{ width: 24, height: 18 }}
                        />
                        <span>{lang.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>
      </ProfileSection>
    </Box>
  );
}

export default Profile;
